import { useEffect, useState } from 'react';

export type Viewport = 'xs' | 's' | 'm' | 'l' | 'xl';

export const BREAKPOINT_MAX_WIDTH_MAP: Record<Viewport, number> = {
  xs: 640,
  s: 960,
  m: 1200,
  l: 1440,
  xl: 1745,
};

/**
 * Hook to check if query matches current viewport. Can be used
 * As it uses native browser features it will automatically notify in case of changes.
 * The initial state of the query will always be false to ensure consistency between ssr and the initial browser render.
 * @param query Query to be passed to window.matchMedia
 */
const useMatchMedia = (query: string): boolean | null => {
  // set match to false to enforce consistent client and server render
  // it would be possible to extend this to simulate a given device when evaluating

  const [match, setMatch] = useState<boolean | null>(null);

  useEffect(() => {
    const queryList = matchMedia(query);

    const update = () => {
      setMatch(queryList.matches);
    };

    update();
    // addListener is deprecated but support will stay for years and is supported also in older browsers.
    // Safari 13 does not support addEventListener.

    queryList.addListener(update);

    return () => {
      queryList.removeListener(update);
    };
  }, [query]);

  return match;
};

/**
 * Helper for useMatchMedia to use queries based on viewport sizes.
 * All sizes greater or equal than the given viewport will match.
 * @param viewport One of the available viewports
 */

type CustomViewport = number;

export const useMaxViewport = (viewport: Viewport | CustomViewport): boolean => {
  const maxWidth = typeof viewport === 'string' ? BREAKPOINT_MAX_WIDTH_MAP[viewport] : viewport;

  return useMatchMedia(`(max-width: ${maxWidth}px)`) || false;
};
